/**
 * useModelIntake — slot state + one-at-a-time upload queue for the `/model` intake surface.
 *
 * Spec: `thinking/design/handoffs/training_shift/Model Intake.html` states A-C (canvas 02).
 * Verification round-trip: plan `2026_07_12_training_serving_system.md` §D.
 *
 * Each added photo becomes a slot in 'verifying' status immediately (optimistic — the shimmer
 * veil), then flips to 'ok' or 'failed' once `POST /api/model/verify` answers. Uploads run
 * strictly one at a time through a single `useFetcher()` instance and an internal queue — the
 * CLAUDE.md streaming contract (`webapp/app/products/narcis/CLAUDE.md`) calls for `useFetcher`
 * over `useNavigation` for any form submission a component drives itself, so busy state here
 * stays local to this hook and never bleeds into router-wide navigation state.
 *
 * Persistence: verified slots' `s3Key`s (not blobs — a File/Blob cannot survive
 * `JSON.stringify`) are written to sessionStorage on every change. Object URLs and file bytes are both
 * gone after a reload, so restored slots render their thumbnail from `/api/model/photo?key=…`
 * (`routes/api.model.photo.tsx`) instead of a blob URL — an authenticated, ownership-checked read
 * of the same S3 object the upload wrote. `photoUrl()` below is the single place that builds it.
 *
 * This hook lives in `@core/` (promoted from `@product/narcis/hooks/` in the W8 ovid port) and
 * must not import a product-specific module directly — two things are injected rather than
 * imported for that reason, both required options on `useModelIntake`:
 *   - `compress`: each product's own `compressImage` (`@product/{narcis,ovid}/utils/imageProcessing.ts`).
 *   - `onEvent`: the GA4 firing for `model_intake_photo_verified`/`model_intake_photo_rejected` —
 *     narcis's caller (`routes/_layout.model.tsx`) passes `narcisTrack` (typed `NarcisEventMap`),
 *     ovid's passes `ovidTrack`, so the fired events stay byte-identical per product (same names,
 *     same params, same `browser_session_id` merge each tracker already does internally).
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {useFetcher} from 'react-router'
import {safeUuid} from '@core/utils/uuid'
import {getProductId} from '@core/products/registry'
import {validImageTypes} from '@core/utils/validations'
import type {PhotoFlag, SetAdvisory, SetCheckResult} from '@core/products/setCheck.server'
import type {GenderGate} from '@core/products/subjectGender'

export const MIN_TRAINING_PHOTOS = 6
export const MAX_TRAINING_PHOTOS = 20

// Product-scoped (was hardcoded 'narcis-model-intake' pre-promotion — a leftover from when this
// hook had a single caller). Harmless in practice today (narcis and ovid run as separate origins,
// so sessionStorage is already partitioned by the browser), but now that this file lives in
// `@core/` and both products import it, the key must not assume there is only one caller.
const STORAGE_KEY = `${getProductId()}-model-intake`

// -------------------------------------------------------------------------------------------
// Calibration constants — single named block (plan `2026_07_13_model_intake_load_shape.md` §A/B).
//
// INTAKE_COMPRESS_*: reuses each product's existing three-tier `compressImage`
// (`@product/{narcis,ovid}/utils/imageProcessing.ts` — narcis's is the same call `useFaceSlots.ts:316`
// already makes for the portrait/pack intake; ovid's is the equivalent, since compressImage's
// fail-open diagnostic fires through the product's own typed analytics tracker, so it can't be a
// single shared function) rather than a new compressor. This @core/ hook takes `compress` as an
// injected dependency (`UseModelIntakeOptions.compress`, same pattern as `onEvent` below) so it
// never hardcodes an import into one product's tree. maxDim sits comfortably above the trainer's
// 832×1216 prep target (job_runner.py:239) so training fidelity is untouched; quality is raised
// from the 0.8 default because these photos ARE the training set and skin texture is the
// identity-loss signal — pay the extra bytes here, the win comes from the dimension cap, not the
// quantiser.
//
// BUSY_RETRY_*: shed-under-load ML admission (`services/mlLock.server.ts`) can shed a training
// verify with a 503 `{verdict: 'busy'}`. Minimal convergent protocol (2026-07-13, second
// adversarial-review scope revision — replaces an earlier exponential-backoff design deemed more
// protocol than this surface needs): on 'busy', re-append the photo to the TAIL of the same serial
// queue after a flat delay, up to a retry cap. Flat 2.5s x 10 attempts is a ~25s total budget,
// sized against MAX_ML_QUEUE_DEPTH's container-derived measurement (see mlLock.server.ts's
// calibration block for the hold-time numbers this was set against) — comfortably past the
// worst-case drain at that depth, without a backoff curve's complexity.
// -------------------------------------------------------------------------------------------
export const INTAKE_COMPRESS_QUALITY = 0.92
export const INTAKE_COMPRESS_MAX_DIM = 1600
export const BUSY_RETRY_DELAY_MS = 2500
export const MAX_BUSY_RETRIES = 10
// -------------------------------------------------------------------------------------------

/** Dependencies `compressAndEnqueue` needs, injected rather than closed over — extracted from the
 *  hook's `enqueueCompressed` callback so the compress-then-enqueue wiring is testable without
 *  rendering the hook itself (this repo's jest project runs `testEnvironment: 'node'` with no
 *  jsdom/@testing-library/react — see the report for why). */
export interface CompressAndEnqueueDeps {
  compress: (file: File, quality: number, maxDim: number) => Promise<File>
  createObjectURL: (file: File) => string
  setPreview: (id: string, previewUrl: string) => void
  enqueue: (id: string, file: File) => void
  pump: () => void
}

/**
 * Compresses `file` at (INTAKE_COMPRESS_QUALITY, INTAKE_COMPRESS_MAX_DIM) and hands the
 * COMPRESSED result — never the original — to the preview and the upload queue. `compressImage`
 * already fail-opens to the original file on every internal tier; the catch here is a last-resort
 * guard against something upstream throwing (e.g. a rejected createObjectURL) so a slot can never
 * strand in 'verifying' forever. No removed-slot guard here — `setPreview`/`enqueue` are keyed
 * updates (a `.map()` over current slots, a queue push keyed by id) that are naturally a no-op if
 * the slot is already gone by the time compression settles; simpler than tracking removal state.
 */
export async function compressAndEnqueue(id: string, file: File, deps: CompressAndEnqueueDeps): Promise<void> {
  let compressed: File
  try {
    compressed = await deps.compress(file, INTAKE_COMPRESS_QUALITY, INTAKE_COMPRESS_MAX_DIM)
  } catch {
    compressed = file
  }
  const previewUrl = deps.createObjectURL(compressed)
  deps.setPreview(id, previewUrl)
  deps.enqueue(id, compressed)
  deps.pump()
}

export type BusyRetryDecision = {retry: true} | {retry: false; result: {verdict: 'failed'; reason: string}}

/**
 * Pure decision for a 'busy' verdict — no timers, no queue, no React state. `attempts` is the
 * retry count AFTER this shed (1-indexed: the first shed passes attempts=1). Retries while at or
 * under MAX_BUSY_RETRIES; past it, falls through to the same retryable-failure shape a genuine
 * rejection uses. The caller (the settle effect below) must NOT fire the rejected-photo GA4 event
 * for the exhaustion case — busy-exhaustion is server load, not a bad photo (see
 * decideVerifyTracking below).
 */
export function decideBusyRetry(attempts: number): BusyRetryDecision {
  if (attempts <= MAX_BUSY_RETRIES) return {retry: true}
  return {retry: false, result: {verdict: 'failed', reason: 'Server busy. Try again.'}}
}

export type VerifyTrackingEvent = 'verified' | 'rejected' | 'none'

/**
 * Which GA4 event (if any) fires for a settled verify result — pure, no side effects. Extracted so
 * the "busy-exhaustion never fires model_intake_photo_rejected" contract has real test coverage
 * instead of living only inside the settle effect's closure (second adversarial review, issue D).
 */
export function decideVerifyTracking(verdict: 'ok' | 'failed', skipRejectedTracking: boolean): VerifyTrackingEvent {
  if (verdict === 'ok') return 'verified'
  if (skipRejectedTracking) return 'none'
  return 'rejected'
}

// Set-level checks (`@core/products/setCheck.server.ts` via `routes/api.model.set-check.tsx`) are soft
// advisories, never a gate — fired on a separate fetcher so they can never interfere with the
// per-photo verify queue's fetcher.state. SET_CHECK_MIN_OK matches the plan's "once okCount >= 2"
// (a single photo has nothing to compare against).
const SET_CHECK_DEBOUNCE_MS = 800
const SET_CHECK_MIN_OK = 2

/** Authenticated, ownership-checked read of a training photo — see routes/api.model.photo.tsx.
 *  Exported so other Training Shift surfaces (the possession block's contact strip) build the
 *  same URL from a bare s3Key instead of re-deriving the path inline. */
export function photoUrl(s3Key: string): string {
  return `/api/model/photo?key=${encodeURIComponent(s3Key)}`
}

export type IntakeSlot =
  | {id: string; status: 'verifying'; previewUrl: string | null}
  | {id: string; status: 'ok'; previewUrl: string | null; s3Key: string}
  | {id: string; status: 'failed'; previewUrl: string | null; reason: string}

type VerifyResponse =
  | {verdict: 'ok'; s3Key: string}
  | {verdict: 'failed'; reason: string}
  // Shed by the sheddable ML lock (HTTP 503) — not a rejection. See BUSY_RETRY_* above.
  | {verdict: 'busy'}

// The stash tolerates shape drift — no version field, just defensive parsing (readStash below): an
// entry from an older build that carries more than `s3Key` still parses.
type StoredSlot = {s3Key: string}

function readStash(): StoredSlot[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((s): s is StoredSlot => typeof s?.s3Key === 'string').map((s) => ({s3Key: s.s3Key}))
  } catch {
    return []
  }
}

function writeStash(slots: IntakeSlot[]) {
  try {
    const okKeys = slots
      .filter((s): s is Extract<IntakeSlot, {status: 'ok'}> => s.status === 'ok')
      .map((s) => ({s3Key: s.s3Key}))
    if (okKeys.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY)
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(okKeys))
    }
  } catch {
    /* ignore — private mode / quota */
  }
}

/** The label a skipped file goes by in its notice: its extension, else its MIME subtype. */
function fileTypeLabel(file: Pick<File, 'name' | 'type'>): string {
  const ext = file.name.includes('.') ? file.name.split('.').pop() : ''
  const label = ext || file.type.split('/').pop() || 'This file type'
  return label.toUpperCase()
}

export interface AddPlan<F extends Pick<File, 'name' | 'type'>> {
  /** Files that become slots. */
  accepted: F[]
  /** Distinct labels of the unsupported file types dropped, and how many files they were. */
  unsupportedLabels: string[]
  unsupportedCount: number
  /** Supported files that did not fit under MAX_TRAINING_PHOTOS. */
  overflowCount: number
}

/**
 * What an add does with a batch of files — pure. Unsupported types are dropped first (they were never
 * photos); the supported ones fill the `room` left, the rest are counted as overflow. Nothing is
 * dropped without being counted, so the caller can say so.
 */
export function planAdd<F extends Pick<File, 'name' | 'type'>>(files: F[], room: number): AddPlan<F> {
  const supported: F[] = []
  const labels: string[] = []
  let unsupportedCount = 0
  for (const file of files) {
    if (validImageTypes.includes(file.type)) {
      supported.push(file)
    } else {
      unsupportedCount++
      const label = fileTypeLabel(file)
      if (!labels.includes(label)) labels.push(label)
    }
  }
  const fit = Math.max(0, room)
  return {
    accepted: supported.slice(0, fit),
    unsupportedLabels: labels,
    unsupportedCount,
    overflowCount: Math.max(0, supported.length - fit),
  }
}

/** The lines an add leaves behind for the files it did not take — empty when it took them all. */
export function skippedNotices(
  plan: Pick<AddPlan<File>, 'unsupportedLabels' | 'unsupportedCount' | 'overflowCount'>,
): string[] {
  const lines: string[] = []
  if (plan.unsupportedCount > 0) {
    const names = plan.unsupportedLabels.join(' and ')
    const verb = plan.unsupportedLabels.length === 1 ? 'is' : 'are'
    lines.push(
      `${plan.unsupportedCount} file${plan.unsupportedCount === 1 ? '' : 's'} skipped: ${names} ${verb} not supported. Use JPG, PNG or WebP.`,
    )
  }
  if (plan.overflowCount > 0) {
    lines.push(
      `${plan.overflowCount} photo${plan.overflowCount === 1 ? '' : 's'} not added: ${MAX_TRAINING_PHOTOS} is the most you can add.`,
    )
  }
  return lines
}

/**
 * A verify answer applied to the slot list — pure. A slot the user removed while its check was in
 * flight is simply absent, so a late answer lands on nothing and is discarded.
 */
export function applyVerifyResult(
  slots: IntakeSlot[],
  id: string,
  result: Exclude<VerifyResponse, {verdict: 'busy'}>,
): IntakeSlot[] {
  return slots.map((s) => {
    if (s.id !== id) return s
    // The blob preview lives as long as the slot (removeSlot revokes it): revoking here left a dead
    // URL in state, and any image mounted later — the sign-in step's strip — broke.
    if (result.verdict === 'ok') return {id, status: 'ok', previewUrl: s.previewUrl, s3Key: result.s3Key}
    return {id, status: 'failed', previewUrl: s.previewUrl, reason: result.reason}
  })
}

export interface UseModelIntakeReturn {
  slots: IntakeSlot[]
  okCount: number
  hasFailures: boolean
  isFull: boolean
  addFiles: (files: File[]) => void
  /** Removes a slot in any status — a photo still being checked included; its late answer is dropped. */
  removeSlot: (id: string) => void
  /** Lines for the files the last add did not take (unsupported type, past the maximum); replaced by
   *  each add, emptied by `clearSkippedNotices`. */
  skippedNotices: string[]
  clearSkippedNotices: () => void
  /** Set-level flags keyed by s3Key — soft, advisory, never gates Start. Empty/absent means
   *  either unflagged or not yet checked. */
  setCheckFlags: Record<string, PhotoFlag[]>
  setAdvisories: SetAdvisory[]
  /** The product's hard refusal on the set as the server last judged it (Ovid: the subject must be a woman),
   *  or null. Narcis's set-check names none, so this stays null there. */
  setGate: Extract<GenderGate, {ok: false}> | null
  /** The `s3Key`s the hook was seeded with at mount via `UseModelIntakeOptions.prefill` (funnel
   *  spec §10, the retrain intake) — empty when no prefill was supplied, or when a sessionStorage
   *  stash restore won out over it (see the mount effect). Frozen for the life of the mount: this
   *  is the manifest a caller diffs the submitted set against (`diffRetrainSet` below) to derive
   *  `retrain_started`'s `photos_kept`/`photos_added`/`photos_removed`, not a live-updating list. */
  prefillKeys: string[]
  /**
   * Drops the sessionStorage stash. The caller MUST call this the moment a Start is accepted
   * (`{status: 'queued'}`) — the stash is a draft buffer for an intake in progress, and Start
   * destroys the very objects it points at (`startTraining` best-effort-deletes every fresh staging
   * key once the per-job copies commit, `@core/products/trainingIntake.server.ts`).
   *
   * Before this existed, the stash outlived its job and, since a non-empty stash wins over
   * `prefill` (see the mount effect), a retrain in the same tab restored twelve slots pointing at
   * deleted objects: the tiles 404 and Start fails the per-job copy, which surfaced as a 500.
   */
  clearStash: () => void
}

/** kept/added/removed between the retrain intake's prefill manifest and the set actually
 *  submitted at Start — pure set arithmetic, no React/DOM, so the `retrain_started` event's
 *  payload (funnel spec §10) has real, isolated coverage independent of the hook's own React
 *  wiring (this repo's jest project has no jsdom — see the file header on `compressAndEnqueue`'s
 *  extraction for the same reasoning). `kept` counts submitted keys that were already in the
 *  prefill; `added` is whatever's left of the submission; `removed` counts prefill keys absent
 *  from the submission. Order-independent, duplicate-safe (both inputs are treated as sets). */
export interface RetrainSetDiff {
  photos_kept: number
  photos_added: number
  photos_removed: number
}

export function diffRetrainSet(prefillKeys: string[], submittedKeys: string[]): RetrainSetDiff {
  const prefillSet = new Set(prefillKeys)
  const submittedSet = new Set(submittedKeys)
  let kept = 0
  for (const key of submittedSet) {
    if (prefillSet.has(key)) kept++
  }
  let removed = 0
  for (const key of prefillSet) {
    if (!submittedSet.has(key)) removed++
  }
  return {photos_kept: kept, photos_added: submittedSet.size - kept, photos_removed: removed}
}

/** The intake-funnel event names this hook fires, keyed to their exact params shape — a map
 *  (not a flat union sharing one shape) because `model_intake_photos_ready` carries different
 *  params than the original verify/reject pair.
 *  A caller's typed tracker (e.g. narcisTrack's NarcisEventMap) must declare each key here with
 *  this exact shape for the `onEvent` assignment below to type-check. */
export type ModelIntakeEventMap = {
  model_intake_photo_verified: {n_verified: number}
  model_intake_photo_rejected: {n_verified: number; reason: string}
  /** Fires once when the uploaded batch settles into a valid, startable set: nothing left in
   *  'verifying' status, and the ok count sits within [MIN_TRAINING_PHOTOS, MAX_TRAINING_PHOTOS].
   *  Guard resets whenever the set falls back out of that range (a removal dropping okCount below
   *  the minimum), so a later genuine re-settle fires again — see the settle effect below. */
  model_intake_photos_ready: {photo_count: number; rejected_count: number}
}
export type ModelIntakeEventName = keyof ModelIntakeEventMap

// Overloaded (not generic) call signature — a generic `<E extends ModelIntakeEventName>(event: E,
// params: ModelIntakeEventMap[E]) => void` looks equivalent but does NOT type-check against
// narcisTrack's own generic `<E extends keyof NarcisEventMap>(...)`: TS compares two independently
// generic function types by requiring one map to be assignable to the other AS A WHOLE (every key
// present both ways), which fails since NarcisEventMap has ~60 keys ModelIntakeEventMap doesn't
// declare. An intersection of concrete overloads sidesteps that — each overload is checked against
// narcisTrack's instantiation independently, which succeeds since NarcisEventMap declares all four
// keys below with matching shapes (verified 2026-07-16 against a real tsc run).
export type ModelIntakeOnEvent =
  & ((event: 'model_intake_photo_verified', params: ModelIntakeEventMap['model_intake_photo_verified']) => void)
  & ((event: 'model_intake_photo_rejected', params: ModelIntakeEventMap['model_intake_photo_rejected']) => void)
  & ((event: 'model_intake_photos_ready', params: ModelIntakeEventMap['model_intake_photos_ready']) => void)

export interface UseModelIntakeOptions {
  /** Optional analytics hook, fired for the intake funnel events (verify/reject, photos-ready).
   *  Injected rather than imported so this @core/ hook never depends on a
   *  product's analytics module — see the file header. */
  onEvent?: ModelIntakeOnEvent
  /** The caller's own `compressImage` (`@product/{narcis,ovid}/utils/imageProcessing.ts`),
   *  injected for the same reason `onEvent` is — this @core/ hook must not import one product's
   *  compressor (its fail-open diagnostic fires through that product's own analytics tracker, so
   *  the two products' functions genuinely differ, not just in import path). Required: every
   *  caller of this hook drives a real upload queue, so silently skipping compression on a missing
   *  option would be a load-shape regression, not a graceful degradation.
   *  See INTAKE_COMPRESS_QUALITY/INTAKE_COMPRESS_MAX_DIM above for the call's arguments. */
  compress: (file: File, quality: number, maxDim: number) => Promise<File>
  /**
   * The retrain intake's prefill (funnel spec §10) — the caller's currently served training set,
   * already verified, seeded as 'ok' slots with NO verify call (badges kept, not re-run). Optional
   * and undefined by default: ovid's `_layout.model.tsx` never passes this, so its behavior stays
   * byte-identical to before this option existed. Only consulted ONCE, at mount, by the same
   * mount-once effect that restores a sessionStorage stash (`restoredRef`) — a non-empty stash
   * (an interrupted first-training upload) wins over this, since that reflects photos the user was
   * actively mid-upload with in THIS browser, closer intent than the server's last-known-served set.
   */
  prefill?: {s3Key: string}[]
  /**
   * A GET route answering `{photos: [{s3Key}]}` — the screened photos the server holds for
   * this browser session. Consulted once at mount, only when the stash is empty and there is no
   * `prefill`: the stash is per-tab, so a visitor who signs in through a magic link opened in
   * another browser arrives with nothing local. Narcis passes it; ovid never does, so its mount is
   * unchanged.
   */
  sessionPhotosUrl?: string
}

export function useModelIntake(options: UseModelIntakeOptions): UseModelIntakeReturn {
  const {onEvent, compress, prefill, sessionPhotosUrl} = options
  const [prefillKeys, setPrefillKeys] = useState<string[]>([])
  const fetcher = useFetcher<VerifyResponse>()
  const setCheckFetcher = useFetcher<SetCheckResult>()
  const [slots, setSlots] = useState<IntakeSlot[]>([])
  const [setCheckFlags, setSetCheckFlags] = useState<Record<string, PhotoFlag[]>>({})
  const [setAdvisories, setSetAdvisories] = useState<SetAdvisory[]>([])
  const [setGate, setSetGate] = useState<Extract<GenderGate, {ok: false}> | null>(null)
  const restoredRef = useRef(false)
  const queueRef = useRef<Array<{id: string; file: File}>>([])
  const uploadingIdRef = useRef<string | null>(null)
  const uploadingFileRef = useRef<File | null>(null)
  const setCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Per-slot busy-retry attempt count (BUSY_RETRY_*).
  const busyRetriesRef = useRef<Record<string, number>>({})
  // Live-updated mirror of `slots`, read from the busy-retry setTimeout below — the ONE place a
  // delayed callback needs to know "does this slot still exist" without a dedicated tombstone set
  // (second adversarial review, scope revision: simplify to a single existence check at requeue
  // time rather than tracking removal state everywhere).
  const slotsRef = useRef<IntakeSlot[]>([])
  // Pending busy-retry timers, cleared on unmount so a delayed requeue can't fire after navigation
  // and submit on a dead hook.
  const busyRetryTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  // Restore verified s3Keys on mount — thumbnails are lost (see file header); the slot still
  // counts toward the 12-photo threshold and toward Start. A non-empty stash wins over `prefill`
  // (see UseModelIntakeOptions.prefill's doc) — otherwise, seed from the retrain prefill when one
  // was supplied. Both branches build the SAME 'ok'-status shape verify would have produced, with
  // no verify call and no re-screening: badges kept, per spec §10.
  useEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true
    const stashed = readStash()
    if (stashed.length > 0) {
      setSlots(stashed.map((s) => ({id: safeUuid(), status: 'ok', previewUrl: photoUrl(s.s3Key), s3Key: s.s3Key})))
      return
    }
    if (prefill && prefill.length > 0) {
      setSlots(prefill.map((p) => ({id: safeUuid(), status: 'ok', previewUrl: photoUrl(p.s3Key), s3Key: p.s3Key})))
      setPrefillKeys(prefill.map((p) => p.s3Key))
      return
    }
    if (sessionPhotosUrl) {
      // Nothing local to restore: ask the server for the photos this browser session already
      // holds. Slots the user added while the request was in flight win — the restore only seeds
      // an empty bench. A failed request leaves the bench empty, same as no restore.
      fetch(sessionPhotosUrl)
        .then((res) => (res.ok ? res.json() : null))
        .then((body: {photos?: StoredSlot[]} | null) => {
          const photos = (body?.photos ?? []).filter((p) => typeof p?.s3Key === 'string')
          if (photos.length === 0) return
          setSlots((current) =>
            current.length > 0
              ? current
              : photos.map((p) => ({
                  id: safeUuid(),
                  status: 'ok' as const,
                  previewUrl: photoUrl(p.s3Key),
                  s3Key: p.s3Key,
                })),
          )
        })
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist on every change (debounce-free — slot counts are small, writes are cheap). Stops for
  // good once `clearStash` has run: the keys are spent from that moment (see its doc), so a later
  // slots change must not write them back.
  const stashClearedRef = useRef(false)
  useEffect(() => {
    if (!restoredRef.current || stashClearedRef.current) return
    writeStash(slots)
  }, [slots])

  const clearStash = useCallback(() => {
    stashClearedRef.current = true
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore — private mode / quota, same posture as writeStash */
    }
  }, [])

  // Keep slotsRef current for the busy-retry timer's existence check (see slotsRef above).
  useEffect(() => {
    slotsRef.current = slots
  }, [slots])

  // NICE E (2026-07-13 adversarial review): clear any pending busy-retry timers on unmount —
  // without this, a timer fires after the user navigates away and calls pump()/fetcher.submit()
  // on a dead hook.
  useEffect(() => {
    return () => {
      busyRetryTimersRef.current.forEach((t) => clearTimeout(t))
      busyRetryTimersRef.current.clear()
    }
  }, [])

  // Set-level checks: soft, non-blocking, fired on setCheckFetcher (never the per-photo verify
  // fetcher) whenever the slot list settles — debounced SET_CHECK_DEBOUNCE_MS so a burst of adds
  // or a verify landing mid-upload doesn't fire one request per change. Gated on
  // SET_CHECK_MIN_OK: nothing to compare with a single photo.
  useEffect(() => {
    if (setCheckTimerRef.current) clearTimeout(setCheckTimerRef.current)
    const okKeys = slots.filter((s): s is Extract<IntakeSlot, {status: 'ok'}> => s.status === 'ok').map((s) => s.s3Key)
    if (okKeys.length < SET_CHECK_MIN_OK) {
      // Below the comparison threshold there is no set to check against — clear any lingering
      // flags from a larger set (removals can strand a stale "different person?" badge otherwise).
      setSetCheckFlags({})
      setSetAdvisories([])
      setSetGate(null)
      return
    }
    setCheckTimerRef.current = setTimeout(() => {
      setCheckFetcher.submit({s3Keys: okKeys}, {method: 'POST', encType: 'application/json', action: '/api/model/set-check'})
    }, SET_CHECK_DEBOUNCE_MS)
    return () => {
      if (setCheckTimerRef.current) clearTimeout(setCheckTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots])

  // Apply the set-check response whenever it settles. Keyed replace, not merge — a set-check
  // reflects the FULL okKeys list it was submitted with, so stale flags on a removed/never-seen
  // key naturally drop out.
  useEffect(() => {
    if (setCheckFetcher.state !== 'idle' || !setCheckFetcher.data) return
    const data = setCheckFetcher.data
    const flagMap: Record<string, PhotoFlag[]> = {}
    for (const photo of data.photos) flagMap[photo.s3Key] = photo.flags
    setSetCheckFlags(flagMap)
    setSetAdvisories(data.set.advisories)
    setSetGate(data.gate && !data.gate.ok ? data.gate : null)
  }, [setCheckFetcher.state, setCheckFetcher.data])

  // Fire-once guard for model_intake_photos_ready — reset whenever the set falls back out of the
  // valid range so a later genuine re-settle (e.g. remove a photo below MIN, add enough back)
  // fires again, rather than only ever once per mount.
  const photosReadyTrackedRef = useRef(false)
  useEffect(() => {
    const stillVerifying = slots.some((s) => s.status === 'verifying')
    if (stillVerifying) {
      photosReadyTrackedRef.current = false
      return
    }
    const okN = slots.filter((s) => s.status === 'ok').length
    const inBounds = okN >= MIN_TRAINING_PHOTOS && okN <= MAX_TRAINING_PHOTOS
    if (!inBounds) {
      photosReadyTrackedRef.current = false
      return
    }
    if (photosReadyTrackedRef.current) return
    photosReadyTrackedRef.current = true
    const rejectedN = slots.filter((s) => s.status === 'failed').length
    onEvent?.('model_intake_photos_ready', {photo_count: okN, rejected_count: rejectedN})
  }, [slots, onEvent])

  const pump = useCallback(() => {
    if (uploadingIdRef.current) return
    const next = queueRef.current.shift()
    if (!next) return
    uploadingIdRef.current = next.id
    uploadingFileRef.current = next.file
    const formData = new FormData()
    formData.append('photo', next.file)
    fetcher.submit(formData, {method: 'POST', encType: 'multipart/form-data', action: '/api/model/verify'})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Drain the queue as each upload settles — fetcher.state flips back to 'idle' with fresh
  // `data` exactly once per submission; uploadingIdRef guards against re-applying the same
  // response if this effect re-runs before the next pump() changes fetcher.state. Note the guard
  // no longer requires fetcher.data to be truthy (see the branch below) — SHOULD C, third
  // adversarial review.
  useEffect(() => {
    if (fetcher.state !== 'idle' || !uploadingIdRef.current) return
    const id = uploadingIdRef.current
    const file = uploadingFileRef.current
    uploadingIdRef.current = null
    uploadingFileRef.current = null

    let result: VerifyResponse
    if (!fetcher.data) {
      // SHOULD C: the fetcher settled to idle with no data — a network drop, an ALB 502, or a
      // deploy landing mid-upload all look like this (no JSON body to parse). Without this
      // branch, uploadingIdRef never clears and pump() bails forever (it only proceeds once
      // uploadingIdRef is null), wedging the REST of the queue in 'verifying' permanently, with no
      // recovery but a reload. Same retryable-failure treatment as any other infra failure (e.g.
      // the 502 "couldn't save" case api.model.verify.tsx already returns for a post-screening S3
      // write failure).
      result = {verdict: 'failed', reason: 'Could not check this photo. Try again.'}
    } else {
      result = fetcher.data
    }
    // Busy-exhaustion is server load, never a bad photo — must not contaminate the rejection
    // funnel even on the rare path where every retry is spent (decideVerifyTracking below).
    let skipRejectedTracking = false

    if (result.verdict === 'busy') {
      const attempts = (busyRetriesRef.current[id] ?? 0) + 1
      const decision = decideBusyRetry(attempts)
      if (decision.retry && file) {
        // Still 'verifying' from the caller's point of view — a slower shimmer, never a false
        // rejection. Re-append to the TAIL of the same serial queue after a flat delay and keep
        // draining any other queued photos in the meantime (pump() below), rather than stalling
        // the whole queue on this one photo.
        busyRetriesRef.current[id] = attempts
        const timerId = setTimeout(() => {
          busyRetryTimersRef.current.delete(timerId)
          // One guard, at requeue time: if the slot no longer exists in state (the user removed
          // it while this was pending), drop silently rather than resurrecting it.
          if (!slotsRef.current.some((s) => s.id === id)) return
          queueRef.current.push({id, file})
          pump()
        }, BUSY_RETRY_DELAY_MS)
        busyRetryTimersRef.current.add(timerId)
        pump()
        return
      }
      // Not retrying — either decideBusyRetry exhausted the budget, or the file is gone
      // (defensive; pump() only ever hands this effect a queued entry with a real file). Falls
      // through as a retryable failure, same copy register as any other rejection (e.g. the 502
      // "couldn't save" case below) — but see skipRejectedTracking above: no GA4 event fires for
      // this specific path.
      delete busyRetriesRef.current[id]
      result = decision.retry ? {verdict: 'failed', reason: 'Server busy. Try again.'} : decision.result
      skipRejectedTracking = true
    } else {
      delete busyRetriesRef.current[id]
    }

    // GA4 funnel: n_verified is the running ok-count read from the pre-transition `slots`
    // closure — the abandonment curve against the photo minimum. A rejection also carries its
    // reason (2026-08-11: the 2026-08-10 abandonment at 9 verified took a log-decode session to
    // explain; the `reason` custom dimension was already registered, only the param was missing).
    const priorOkCount = slots.filter((s) => s.status === 'ok').length
    const tracking = decideVerifyTracking(result.verdict === 'ok' ? 'ok' : 'failed', skipRejectedTracking)
    if (tracking === 'verified') {
      onEvent?.('model_intake_photo_verified', {n_verified: priorOkCount + 1})
    } else if (tracking === 'rejected') {
      onEvent?.('model_intake_photo_rejected', {n_verified: priorOkCount, reason: result.verdict === 'failed' ? result.reason : ''})
    }
    const settled = result
    setSlots((prev) => applyVerifyResult(prev, id, settled))
    pump()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.state, fetcher.data])

  const enqueueCompressed = useCallback(
    (id: string, file: File) => {
      void compressAndEnqueue(id, file, {
        compress,
        createObjectURL: (f) => URL.createObjectURL(f),
        setPreview: (previewId, previewUrl) =>
          setSlots((cur) => cur.map((s) => (s.id === previewId && s.status === 'verifying' ? {...s, previewUrl} : s))),
        enqueue: (queueId, f) => queueRef.current.push({id: queueId, file: f}),
        pump,
      })
    },
    [pump, compress],
  )

  // What the bench holds right now, updated synchronously by add and remove so two calls in one tick
  // (a drop and a paste) each see the room the other left. `slots` follows it a render later.
  const benchRef = useRef<IntakeSlot[]>([])
  useEffect(() => {
    benchRef.current = slots
  }, [slots])

  const [skipped, setSkipped] = useState<string[]>([])
  const clearSkippedNotices = useCallback(() => setSkipped([]), [])

  const addFiles = useCallback(
    (files: File[]) => {
      const plan = planAdd(files, MAX_TRAINING_PHOTOS - benchRef.current.length)
      setSkipped(skippedNotices(plan))
      if (plan.accepted.length === 0) return
      const added: IntakeSlot[] = plan.accepted.map(() => ({id: safeUuid(), status: 'verifying', previewUrl: null}))
      benchRef.current = [...benchRef.current, ...added]
      setSlots((prev) => [...prev, ...added])
      plan.accepted.forEach((file, i) => enqueueCompressed(added[i].id, file))
    },
    [enqueueCompressed],
  )

  // Kick the queue whenever it grows — addFiles above only enqueues; this effect is the one
  // actor that calls pump(), keeping "one upload in flight" true even under React StrictMode's
  // double-invoke (a second addFiles call before the first pump() lands just grows the queue).
  useEffect(() => {
    pump()
  }, [slots.length, pump])

  const removeSlot = useCallback((id: string) => {
    delete busyRetriesRef.current[id]
    const target = benchRef.current.find((s) => s.id === id)
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
    // The server keeps a removed photo unless told; the restore would bring it back. It refuses
    // keys that are not staged photos (a retrain prefill's per-job copies).
    if (target?.status === 'ok') void fetch(photoUrl(target.s3Key), {method: 'DELETE'}).catch(() => {})
    benchRef.current = benchRef.current.filter((s) => s.id !== id)
    setSlots((prev) => prev.filter((s) => s.id !== id))
    // A photo removed mid-check lets go of the upload: its answer, whenever it comes, finds no
    // upload to settle and is dropped, and the next queued photo takes the fetcher (a new submit
    // interrupts the old request) — a hung check no longer holds the queue.
    if (uploadingIdRef.current === id) {
      uploadingIdRef.current = null
      uploadingFileRef.current = null
      pump()
    }
    // Drop any queued-but-not-yet-uploaded entry immediately — the busy-retry timer's own
    // existence check (slotsRef, above) covers the delayed-requeue case; this covers the
    // synchronous one (a slot removed while still sitting in queueRef).
    queueRef.current = queueRef.current.filter((q) => q.id !== id)
  }, [pump])

  const okCount = slots.filter((s) => s.status === 'ok').length
  const hasFailures = slots.some((s) => s.status === 'failed')
  const isFull = slots.length >= MAX_TRAINING_PHOTOS

  return {
    slots,
    okCount,
    hasFailures,
    isFull,
    addFiles,
    removeSlot,
    skippedNotices: skipped,
    clearSkippedNotices,
    setCheckFlags,
    setAdvisories,
    setGate,
    prefillKeys,
    clearStash,
  }
}
